/*
 * DSD Classification Layer - p11dz145
 * Governance-only classification contract for canonical social objects.
 * Defines categories, tags, content labels, discovery labels, universe/galaxy
 * classification inheritance, and future Multiverse search/filter readiness
 * without enabling Firebase reads, Firebase writes, realtime listeners,
 * recommendation ranking, enforcement, or new renderers.
 */
(function(){
  'use strict';
  if (window.DSDClassificationLayer && window.DSDClassificationLayer.version) return;

  var VERSION = 'p11dz145-classification-layer-v1';
  var OWNER = 'ClassificationLayer';
  var FIREBASE_GATE = 'disabled-classification-governance-only';

  function claim(surface, meta){
    try {
      if (window.DSDRegistry && typeof window.DSDRegistry.claim === 'function') {
        window.DSDRegistry.claim(surface, OWNER, Object.assign({ version: VERSION }, meta || {}));
      }
    } catch(e) {}
  }

  function exists(name){ return !!window[name]; }

  var categoryCatalog = {
    nightlife: { key: 'nightlife', label: 'Nightlife', universeHint: ['nightlife','local'], adultSensitiveAllowed: true },
    local: { key: 'local', label: 'Local', universeHint: ['local'], adultSensitiveAllowed: true },
    fashion: { key: 'fashion', label: 'Fashion / Underwear', universeHint: ['fashion','creators'], adultSensitiveAllowed: true },
    creators: { key: 'creators', label: 'Creators', universeHint: ['creators'], adultSensitiveAllowed: true },
    media: { key: 'media', label: 'Movies / Media', universeHint: ['media'], adultSensitiveAllowed: false },
    fitness: { key: 'fitness', label: 'Fitness', universeHint: ['fitness','local'], adultSensitiveAllowed: true },
    gaming: { key: 'gaming', label: 'Gaming', universeHint: ['gaming'], adultSensitiveAllowed: false },
    travel: { key: 'travel', label: 'Travel', universeHint: ['travel','local'], adultSensitiveAllowed: true },
    photography: { key: 'photography', label: 'Photography', universeHint: ['creators','media'], adultSensitiveAllowed: true },
    events: { key: 'events', label: 'Events', universeHint: ['local','nightlife'], adultSensitiveAllowed: true },
    dating: { key: 'dating', label: 'Dating / Social', universeHint: ['local','nightlife'], adultSensitiveAllowed: true },
    shopping: { key: 'shopping', label: 'Shopping / Products', universeHint: ['fashion'], adultSensitiveAllowed: false },
    pets: { key: 'pets', label: 'Pets', universeHint: ['local'], adultSensitiveAllowed: false },
    technology: { key: 'technology', label: 'Technology', universeHint: ['creators'], adultSensitiveAllowed: false },
    relationships: { key: 'relationships', label: 'Relationships', universeHint: ['local','creators'], adultSensitiveAllowed: true }
  };

  var discoveryScopes = {
    public: { key: 'public', label: 'Public', searchable: true, recommended: true },
    members: { key: 'members', label: 'Members', searchable: true, recommended: false },
    private: { key: 'private', label: 'Private', searchable: false, recommended: false },
    inviteOnly: { key: 'inviteOnly', label: 'Invite Only', searchable: false, recommended: false },
    hidden: { key: 'hidden', label: 'Hidden', searchable: false, recommended: false },
    premium: { key: 'premium', label: 'Premium', searchable: true, recommended: false },
    event: { key: 'event', label: 'Event', searchable: true, recommended: true },
    adult: { key: 'adult', label: 'Adult Discovery', searchable: true, recommended: false, adultGateRequired: true },
    sensitive: { key: 'sensitive', label: 'Sensitive Discovery', searchable: true, recommended: false, sensitiveGateRequired: true }
  };

  var contentLabels = {
    safe: { key: 'safe', label: 'Safe', moderationRating: 'safe' },
    sensitive: { key: 'sensitive', label: 'Sensitive', moderationRating: 'sensitive' },
    mature: { key: 'mature', label: 'Mature', moderationRating: 'mature' },
    adult: { key: 'adult', label: 'Adult', moderationRating: 'adult' },
    restricted: { key: 'restricted', label: 'Restricted', moderationRating: 'restricted' }
  };

  var socialObjectDefaults = {
    post: { categories: [], tags: [], contentLabels: ['safe'], discoveryScopes: ['public'], universeScopes: [], galaxyScopes: [] },
    profile: { categories: [], tags: [], contentLabels: ['safe'], discoveryScopes: ['members'], universeScopes: [], galaxyScopes: [] },
    story: { categories: [], tags: [], contentLabels: ['safe'], discoveryScopes: ['members'], universeScopes: [], galaxyScopes: [] },
    universe: { categories: [], tags: [], contentLabels: ['safe'], discoveryScopes: ['public'], universeScopes: [], galaxyScopes: [] },
    galaxy: { categories: [], tags: [], contentLabels: ['safe'], discoveryScopes: ['public'], universeScopes: [], galaxyScopes: [] },
    galaxyChatMessage: { categories: [], tags: [], contentLabels: ['inherits-galaxy'], discoveryScopes: ['private'], universeScopes: [], galaxyScopes: [] },
    event: { categories: ['events'], tags: [], contentLabels: ['safe'], discoveryScopes: ['event'], universeScopes: [], galaxyScopes: [] },
    comment: { categories: [], tags: [], contentLabels: ['inherits-post'], discoveryScopes: ['inherits-post'], universeScopes: [], galaxyScopes: [] },
    reaction: { categories: [], tags: [], contentLabels: ['inherits-post'], discoveryScopes: ['inherits-post'], universeScopes: [], galaxyScopes: [] }
  };

  var classificationDoctrine = [
    'Classification is metadata on canonical social objects; it does not create separate renderers, feeds, modals, stores, or Firebase paths.',
    'A social object may belong to multiple categories, tags, discovery scopes, universe scopes, and galaxy scopes.',
    'Universes organize broad culture lanes; galaxies organize communities inside or across universes.',
    'Adult/sensitive labels must align with ModerationMetadata content ratings before discovery or recommendations can use them.',
    'Multiverse search must use classification metadata as a filter layer, not as a separate search architecture.',
    'Galaxy classification may inherit from universe classification but platform governance may override unsafe or misclassified objects.',
    'No Firebase reads, writes, realtime listeners, recommendations, ranking, or enforcement are enabled by this layer.'
  ];

  function arrayify(value){
    if (Array.isArray(value)) return value;
    if (value === undefined || value === null || value === '') return [];
    return [value];
  }

  function slug(value){
    return String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/^-+|-+$/g,'');
  }

  function uniqueClean(values, allowUnknown){
    var seen = {};
    return arrayify(values).map(function(v){ return slug(v); }).filter(function(v){
      if (!v || seen[v]) return false;
      if (!allowUnknown && !categoryCatalog[v] && !discoveryScopes[v] && !contentLabels[v] && String(v).indexOf('inherits-') !== 0) return false;
      seen[v] = true;
      return true;
    });
  }

  function normalizeTags(tags){
    var seen = {};
    return arrayify(tags).map(function(t){ return slug(t); }).filter(function(t){
      if (!t || t.length > 48 || seen[t]) return false;
      seen[t] = true;
      return true;
    }).slice(0, 24);
  }

  function getObjectType(object, context){
    object = object || {};
    context = context || {};
    return object.objectType || object.type || context.objectType || 'post';
  }

  function deriveContentLabels(object, context, defaults){
    var labels = [].concat(defaults.contentLabels || [], object.contentLabels || [], object.contentRating || [], context.contentLabels || [], context.contentRating || []);
    var normalized = uniqueClean(labels, false);
    return normalized.length ? normalized : ['safe'];
  }

  function deriveDiscoveryScopes(object, context, defaults){
    var scopes = [].concat(defaults.discoveryScopes || [], object.discoveryScopes || [], object.visibility || [], context.discoveryScopes || [], context.visibility || []);
    var normalized = uniqueClean(scopes, false);
    return normalized.length ? normalized : ['public'];
  }

  function normalizeClassification(object, context){
    object = object || {};
    context = context || {};
    var objectType = getObjectType(object, context);
    var defaults = socialObjectDefaults[objectType] || socialObjectDefaults.post;
    var categories = uniqueClean([].concat(defaults.categories || [], object.categories || [], object.category || [], context.categories || [], context.category || []), false);
    var tags = normalizeTags([].concat(defaults.tags || [], object.tags || [], context.tags || []));
    var content = deriveContentLabels(object, context, defaults);
    var discovery = deriveDiscoveryScopes(object, context, defaults);
    var universeScopes = uniqueClean([].concat(defaults.universeScopes || [], object.universeScopes || [], object.universes || [], object.universe || [], context.universeScopes || [], context.universes || []), true);
    var galaxyScopes = uniqueClean([].concat(defaults.galaxyScopes || [], object.galaxyScopes || [], object.galaxies || [], object.galaxy || [], context.galaxyScopes || [], context.galaxies || []), true);
    var moderationMetadata = null;

    try {
      if (window.DSDModerationMetadata && typeof window.DSDModerationMetadata.normalizeMetadata === 'function') {
        moderationMetadata = window.DSDModerationMetadata.normalizeMetadata(Object.assign({}, object, {
          objectType: objectType,
          contentRating: content[0] || 'safe'
        }), context);
      }
    } catch(e) {}

    var searchTokens = uniqueClean([].concat(categories, tags, content, discovery, universeScopes, galaxyScopes), true);

    return {
      version: VERSION,
      objectType: objectType,
      objectId: object.postId || object.profileId || object.storyId || object.commentId || object.id || object.objectId || null,
      categories: categories,
      tags: tags,
      contentLabels: content,
      discoveryScopes: discovery,
      universeScopes: universeScopes,
      galaxyScopes: galaxyScopes,
      searchTokens: searchTokens,
      moderationMetadata: moderationMetadata,
      multiverseReady: true,
      universeReady: true,
      galaxyReady: true,
      firebaseReadsEnabled: false,
      firebaseWritesEnabled: false,
      realtimeEnabled: false,
      enforcementMode: 'classification-contract-only'
    };
  }

  function getReport(reason){
    return {
      version: VERSION,
      reason: reason || 'manual-report',
      owner: OWNER,
      firebaseGate: FIREBASE_GATE,
      firebaseReadsEnabled: false,
      firebaseWritesEnabled: false,
      realtimeEnabled: false,
      rankingEnabled: false,
      recommendationEnabled: false,
      enforcementEnabled: false,
      doctrine: classificationDoctrine,
      categoryCatalog: categoryCatalog,
      discoveryScopes: discoveryScopes,
      contentLabels: contentLabels,
      socialObjectDefaults: socialObjectDefaults,
      dependencyReadiness: {
        DSDRegistry: exists('DSDRegistry'),
        SurfaceRegistry: exists('DSDSurfaceRegistry'),
        VisibilityLayer: exists('DSDVisibilityLayer'),
        ModerationMetadata: exists('DSDModerationMetadata'),
        FirebaseSocialSchema: exists('DSDFirebaseSocialSchema'),
        RealtimeOwnershipAudit: exists('DSDRealtimeOwnershipAudit'),
        firebaseReadsEnabled: false,
        firebaseWritesEnabled: false,
        realtimeEnabled: false
      },
      nextGate: 'p11dz146-membership-registry-or-controlled-read-only-post-activation-gate'
    };
  }

  function publish(reason){
    var report = getReport(reason || 'publish');
    claim('classification-layer:doctrine', { doctrine: classificationDoctrine, count: classificationDoctrine.length });
    claim('classification-layer:categories', { categories: categoryCatalog, count: Object.keys(categoryCatalog).length });
    claim('classification-layer:discovery-scopes', { scopes: discoveryScopes, count: Object.keys(discoveryScopes).length });
    claim('classification-layer:content-labels', { labels: contentLabels, count: Object.keys(contentLabels).length });
    claim('classification-layer:object-defaults', socialObjectDefaults);
    claim('classification-layer:dependency-readiness', report.dependencyReadiness);
    claim('classification-layer:future-ecosystem-readiness', {
      multiverse: true,
      universes: true,
      galaxies: true,
      galaxyStories: true,
      galaxyChat: true,
      events: true
    });
    claim('classification-layer:firebase-firewall', {
      firebaseReadsEnabled: false,
      firebaseWritesEnabled: false,
      realtimeEnabled: false,
      rankingEnabled: false,
      recommendationEnabled: false,
      enforcementEnabled: false,
      gate: FIREBASE_GATE
    });
    try { window.dispatchEvent(new CustomEvent('dsd:classification-layer-ready', { detail: report })); } catch(e) {}
    window.DSDClassificationLayerReport = report;
    return report;
  }

  window.DSDClassificationLayer = {
    version: VERSION,
    owner: OWNER,
    getReport: getReport,
    publish: publish,
    categoryCatalog: categoryCatalog,
    discoveryScopes: discoveryScopes,
    contentLabels: contentLabels,
    socialObjectDefaults: socialObjectDefaults,
    normalizeClassification: normalizeClassification,
    normalizeTags: normalizeTags,
    slug: slug
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function(){ publish('dom-ready'); }, { once: true });
  } else {
    publish('already-ready');
  }
})();
